import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Mail,
  Download,
  FileText,
  CheckCircle,
  X,
  AlertCircle,
  Loader,
  ExternalLink,
  Settings,
  User,
  Calendar,
  HardDrive
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import toast from 'react-hot-toast'

import { useAuth } from '@/context/AuthContext'
import {
  isMicrosoftConfigured,
  getMicrosoftSetupInstructions,
  signInWithMicrosoft,
  signOutMicrosoft,
  getCurrentAccount,
  searchMedicalEmails,
  downloadAttachment,
  EmailAttachment
} from '@/lib/microsoft-graph'
import { extractDataFromDocument, uploadDocumentFile, createMedicalDocument } from '@/lib/medical-documents'
import type { MedicalDocumentInput, DocumentType } from '@/types/health'

interface ProcessingAttachment extends EmailAttachment {
  status: 'downloading' | 'extracting' | 'uploading' | 'completed' | 'error'
  error?: string
  extractedData?: any
}

export default function OutlookImporter() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const [showImporter, setShowImporter] = useState(false)
  const [showSetupInstructions, setShowSetupInstructions] = useState(false)
  const [isSignedIn, setIsSignedIn] = useState(false)
  const [userAccount, setUserAccount] = useState(getCurrentAccount())
  const [foundAttachments, setFoundAttachments] = useState<EmailAttachment[]>([])
  const [processingAttachments, setProcessingAttachments] = useState<ProcessingAttachment[]>([])
  const [isSearching, setIsSearching] = useState(false)

  // Recherche des emails médicaux
  const searchEmailsMutation = useMutation({
    mutationFn: searchMedicalEmails,
    onSuccess: (attachments) => {
      setFoundAttachments(attachments)
      toast.success(`${attachments.length} document(s) médical(aux) trouvé(s) !`)
    },
    onError: (error) => {
      console.error('Erreur recherche:', error)
      toast.error('Erreur lors de la recherche dans Outlook')
    },
    onSettled: () => {
      setIsSearching(false)
    }
  })

  // Connexion Microsoft
  const handleSignIn = async () => {
    try {
      const result = await signInWithMicrosoft()
      setIsSignedIn(true)
      setUserAccount(getCurrentAccount())
      toast.success(`Connecté à ${result.account?.username}`)
    } catch (error) {
      console.error('Erreur connexion Microsoft:', error)
      toast.error('Erreur de connexion à Microsoft')
    }
  }

  // Déconnexion
  const handleSignOut = async () => {
    try {
      await signOutMicrosoft()
      setIsSignedIn(false)
      setUserAccount(null)
      setFoundAttachments([])
      setProcessingAttachments([])
      toast.success('Déconnecté de Microsoft')
    } catch (error) {
      console.error('Erreur déconnexion:', error)
      toast.error('Erreur de déconnexion')
    }
  }

  // Recherche des emails
  const handleSearchEmails = () => {
    if (!isSignedIn && !getCurrentAccount()) {
      toast.error('Veuillez vous connecter à Microsoft d\'abord')
      return
    }

    setIsSearching(true)
    searchEmailsMutation.mutate()
  }

  // Import des documents sélectionnés
  const handleImportSelected = async (selectedAttachments: EmailAttachment[]) => {
    if (!user) return

    // Initialiser le traitement
    const initialProcessing: ProcessingAttachment[] = selectedAttachments.map(att => ({
      ...att,
      status: 'downloading'
    }))
    setProcessingAttachments(initialProcessing)

    // Traiter chaque document
    for (let i = 0; i < selectedAttachments.length; i++) {
      const attachment = selectedAttachments[i]

      try {
        // Étape 1: Téléchargement
        setProcessingAttachments(prev => prev.map((pa, index) =>
          index === i ? { ...pa, status: 'downloading' } : pa
        ))

        const attachmentData = await downloadAttachment(attachment.emailId, attachment.id)

        // Créer un File object
        const file = new File([attachmentData], attachment.name, {
          type: 'application/pdf'
        })

        // Étape 2: Upload du fichier dans Supabase Storage
        setProcessingAttachments(prev => prev.map((pa, index) =>
          index === i ? { ...pa, status: 'uploading' } : pa
        ))

        const documentUrl = await uploadDocumentFile(file, user.id)

        // Étape 3: Extraction avec Claude via AWS Bedrock
        setProcessingAttachments(prev => prev.map((pa, index) =>
          index === i ? { ...pa, status: 'extracting' } : pa
        ))

        const extractedData = await extractDataFromDocument(file, user.id)

        // Étape 4: Création du document dans la base
        const documentInput: MedicalDocumentInput = {
          patient_id: user.id,
          title: extractedData.title || attachment.name.replace('.pdf', ''),
          document_type: extractedData.document_type || 'consultation',
          document_date: extractedData.document_date || new Date().toISOString().split('T')[0],
          doctor_name: extractedData.doctor_name,
          specialty: extractedData.specialty,
          content_summary: extractedData.content_summary,
          key_findings: extractedData.key_findings || [],
          recommendations: extractedData.recommendations || [],
          urgency_level: extractedData.urgency_level || 'normal',
          document_url: documentUrl,
          is_urgent: extractedData.urgency_level === 'high',
          metadata: {
            source: 'outlook_import',
            email_subject: attachment.emailSubject,
            email_from: attachment.emailFrom,
            email_date: attachment.emailDate,
            original_filename: attachment.name
          }
        }

        await createMedicalDocument(documentInput)

        // Succès
        setProcessingAttachments(prev => prev.map((pa, index) =>
          index === i ? {
            ...pa,
            status: 'completed',
            extractedData: extractedData
          } : pa
        ))

        toast.success(`${attachment.name} importé avec succès !`)

      } catch (error) {
        console.error(`Erreur import ${attachment.name}:`, error)

        setProcessingAttachments(prev => prev.map((pa, index) =>
          index === i ? {
            ...pa,
            status: 'error',
            error: error instanceof Error ? error.message : 'Erreur inconnue'
          } : pa
        ))

        toast.error(`Erreur avec ${attachment.name}`)
      }
    }

    // Rafraîchir la liste des documents
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['medical-documents'] })
      setFoundAttachments([])
      setProcessingAttachments([])
    }, 2000)
  }

  if (!isMicrosoftConfigured()) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => setShowSetupInstructions(!showSetupInstructions)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          <Settings className="w-4 h-4" />
          {showSetupInstructions ? 'Masquer la configuration' : 'Configurer l\'import Outlook'}
        </button>

        <AnimatePresence>
          {showSetupInstructions && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="bg-blue-900/20 border border-blue-800/40 rounded-xl p-6">
                <div className="flex items-start gap-3 mb-4">
                  <ExternalLink className="w-5 h-5 text-blue-400 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-blue-300 mb-2">Configuration Microsoft Azure (Gratuite)</h3>
                    <pre className="text-xs text-blue-200/80 whitespace-pre-wrap leading-relaxed">
                      {getMicrosoftSetupInstructions()}
                    </pre>
                  </div>
                </div>
                <div className="pt-4 border-t border-blue-800/40">
                  <p className="text-xs text-blue-200/60">
                    ⚠️ Redémarrer l'application après avoir ajouté la clé dans .env
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <button
        onClick={() => setShowImporter(!showImporter)}
        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
      >
        <Mail className="w-4 h-4" />
        {showImporter ? 'Masquer l\'import Outlook' : 'Importer depuis Outlook'}
      </button>

      <AnimatePresence>
        {showImporter && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="bg-dark-surface border border-dark-border rounded-xl p-6 space-y-6">
              {/* Connexion Microsoft */}
              <div className="flex items-center justify-between p-4 bg-dark-surface-high rounded-lg border border-dark-border">
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-blue-400" />
                  <div>
                    <p className="font-medium text-white">
                      {userAccount ? userAccount.username : 'Non connecté'}
                    </p>
                    <p className="text-xs text-slate-400">
                      {userAccount ? 'Connecté à Microsoft' : 'Connectez-vous pour importer vos documents'}
                    </p>
                  </div>
                </div>

                {userAccount ? (
                  <button
                    onClick={handleSignOut}
                    className="btn-secondary text-sm"
                  >
                    Déconnecter
                  </button>
                ) : (
                  <button
                    onClick={handleSignIn}
                    className="btn-primary text-sm"
                  >
                    Se connecter
                  </button>
                )}
              </div>

              {/* Recherche */}
              {(userAccount || isSignedIn) && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-white">Recherche de documents médicaux</h3>
                      <p className="text-sm text-slate-400 mt-1">
                        Recherche automatique dans vos emails des 6 derniers mois
                      </p>
                    </div>

                    <button
                      onClick={handleSearchEmails}
                      disabled={isSearching}
                      className="btn-primary text-sm flex items-center gap-2"
                    >
                      {isSearching ? (
                        <Loader className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      {isSearching ? 'Recherche...' : 'Rechercher'}
                    </button>
                  </div>

                  {/* Mots-clés recherchés */}
                  <div className="bg-dark-surface-high border border-dark-border rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-2">Mots-clés recherchés :</p>
                    <div className="flex flex-wrap gap-1">
                      {['analyse', 'résultats', 'ordonnance', 'compte rendu', 'bilan', 'laboratoire'].map(keyword => (
                        <span key={keyword} className="px-2 py-1 bg-blue-900/30 text-blue-300 text-xs rounded-md">
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Résultats de recherche */}
              {foundAttachments.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-white">
                      {foundAttachments.length} document(s) trouvé(s)
                    </h4>
                    <button
                      onClick={() => handleImportSelected(foundAttachments)}
                      className="btn-primary text-sm"
                    >
                      Importer tout
                    </button>
                  </div>

                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {foundAttachments.map((attachment, index) => (
                      <motion.div
                        key={`${attachment.id}-${index}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center gap-4 p-3 bg-dark-surface-high border border-dark-border rounded-lg"
                      >
                        <FileText className="w-8 h-8 text-red-400 flex-shrink-0" />

                        <div className="flex-1 min-w-0">
                          <h5 className="font-medium text-white truncate">{attachment.name}</h5>
                          <p className="text-sm text-slate-400 truncate">{attachment.emailSubject}</p>
                          <div className="flex items-center gap-4 mt-1">
                            <span className="text-xs text-slate-500">
                              De: {attachment.emailFrom}
                            </span>
                            <span className="text-xs text-slate-500">
                              <Calendar className="w-3 h-3 inline mr-1" />
                              {format(new Date(attachment.emailDate), 'd MMM yyyy', { locale: fr })}
                            </span>
                            <span className="text-xs text-slate-500">
                              <HardDrive className="w-3 h-3 inline mr-1" />
                              {(attachment.size / 1024 / 1024).toFixed(1)} MB
                            </span>
                          </div>
                        </div>

                        <button
                          onClick={() => handleImportSelected([attachment])}
                          className="btn-primary text-sm"
                        >
                          Importer
                        </button>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* Traitement en cours */}
              <AnimatePresence>
                {processingAttachments.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="space-y-4"
                  >
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-white">Importation en cours</h4>
                      <button
                        onClick={() => setProcessingAttachments([])}
                        className="text-slate-400 hover:text-white transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    {processingAttachments.map((attachment, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="flex items-center gap-4 p-3 bg-dark-surface border border-dark-border rounded-lg"
                      >
                        <FileText className="w-5 h-5 text-red-400 flex-shrink-0" />

                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm truncate">{attachment.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {attachment.status === 'downloading' && (
                              <>
                                <Loader className="w-3 h-3 text-blue-400 animate-spin" />
                                <span className="text-xs text-blue-400">Téléchargement...</span>
                              </>
                            )}
                            {attachment.status === 'extracting' && (
                              <>
                                <Loader className="w-3 h-3 text-yellow-400 animate-spin" />
                                <span className="text-xs text-yellow-400">Extraction Claude IA...</span>
                              </>
                            )}
                            {attachment.status === 'uploading' && (
                              <>
                                <Loader className="w-3 h-3 text-purple-400 animate-spin" />
                                <span className="text-xs text-purple-400">Sauvegarde...</span>
                              </>
                            )}
                            {attachment.status === 'completed' && (
                              <>
                                <CheckCircle className="w-3 h-3 text-green-400" />
                                <span className="text-xs text-green-400">
                                  Importé: {attachment.extractedData?.title || attachment.name}
                                </span>
                              </>
                            )}
                            {attachment.status === 'error' && (
                              <>
                                <AlertCircle className="w-3 h-3 text-red-400" />
                                <span className="text-xs text-red-400">{attachment.error}</span>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="text-xs text-slate-400">
                          {(attachment.size / 1024 / 1024).toFixed(1)} MB
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}